import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Download, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ResumeForm } from "../components/ResumeForm";
import { PhotoUpload } from "../components/PhotoUpload";
import { AccentPicker } from "../components/AccentPicker";
import { ResumePreview } from "../components/ResumePreview";
import { TemplatePicker } from "../components/TemplatePicker";
import { api, errMsg } from "../lib/api";
import { downloadPdf } from "../lib/download";
import { useAuth } from "../context/AuthContext";

const useAutosave = (id, resume, onSaved) => {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const first = useRef(true);
  const pending = useRef(null);
  const save = (r) => api.put(`/resumes/${id}`, { title: r.title, template: r.template, data: r.data, show_photo: r.show_photo ?? true, accent: r.accent || null });
  useEffect(() => {
    if (!resume) return;
    if (first.current) { first.current = false; return; }
    setSaving(true);
    pending.current = resume;
    const t = setTimeout(async () => {
      pending.current = null;
      try { await save(resume); setFailed(false); onSaved(); } catch (e) { setFailed(true); toast.error(errMsg(e, "Could not save")); }
      setSaving(false);
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, resume, onSaved]);
  // Flush unsaved edits when leaving the builder.
  useEffect(() => () => { if (pending.current) save(pending.current).catch(() => {}); }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  return { saving, failed };
};

export default function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resume, setResume] = useState(null);
  const [version, setVersion] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const bump = useRef(() => setVersion((v) => v + 1)).current;
  const { saving, failed } = useAutosave(id, resume, bump);

  useEffect(() => {
    api.get(`/resumes/${id}`).then((r) => setResume((cur) => cur || r.data)).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  const download = async () => {
    if (!user.has_access) return navigate("/pricing");
    setDownloading(true);
    try { await downloadPdf(id); toast.success("PDF downloaded"); } catch (e) { toast.error(errMsg(e)); }
    setDownloading(false);
  };

  if (!resume) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;

  return (
    <main className="max-w-[1500px] mx-auto px-5 sm:px-8 py-8 relative z-10" data-testid="builder-page">
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <Link to="/dashboard" className="text-slate-400 hover:text-white" data-testid="builder-back-link"><ArrowLeft className="h-5 w-5" /></Link>
        <Input value={resume.title} onChange={(e) => setResume({ ...resume, title: e.target.value })} data-testid="resume-title-input"
          className="max-w-xs bg-transparent border-transparent hover:border-white/10 font-display text-xl font-semibold" />
        <span className="text-xs font-mono text-slate-500 flex items-center gap-1.5" data-testid="save-status">
          {saving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving</> : failed ? <span className="text-red-400">Not saved</span> : <><Check className="h-3 w-3 text-emerald-400" /> Saved</>}
        </span>
        <Button onClick={download} disabled={downloading || saving} data-testid="builder-download-btn"
          className="ml-auto rounded-full h-11 px-6 bg-emerald-500 text-black font-semibold hover:bg-emerald-400 hover:text-black">
          {user.has_access ? <Download className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
          {user.has_access ? (downloading ? "Preparing…" : "Download PDF") : "Unlock PDF download"}
        </Button>
      </div>
      <div className="grid lg:grid-cols-12 gap-8">
        <section className="lg:col-span-5 space-y-8">
          <div className="rounded-2xl border border-white/10 bg-[#12151E] p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">Template</p>
            <TemplatePicker value={resume.template} onChange={(t) => setResume((r) => ({ ...r, template: t }))} />
            <AccentPicker value={resume.accent} onChange={(c) => setResume((r) => ({ ...r, accent: c }))} />
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#12151E] p-6">
            <PhotoUpload resumeId={id} photoPath={resume.photo_path} showPhoto={resume.show_photo ?? true}
              onToggle={(v) => setResume((r) => ({ ...r, show_photo: v }))}
              onChange={(p) => { setResume((r) => ({ ...r, photo_path: p })); bump(); }} />
            <ResumeForm resumeId={id} data={resume.data}
              onChange={(d) => setResume((r) => ({ ...r, data: typeof d === "function" ? d(r.data) : d }))} />
          </div>
        </section>
        <section className="lg:col-span-7">
          <div className="lg:sticky lg:top-24 max-h-[calc(100vh-7rem)] overflow-auto rounded-xl">
            <ResumePreview resumeId={id} version={version} unlocked={user.has_access} />
          </div>
        </section>
      </div>
    </main>
  );
}
